/**
 * TaskDetail — a task, or an AI suggestion, in the shared detail container.
 *
 * The core editable field set — title, description, status, priority, due
 * date + time, project, assignee — with Save / Complete / Archive. A
 * SUGGESTION opens the same form with Accept / Dismiss instead.
 *
 * Collaborators, comments and attachments hang off a SAVED task, so they load
 * lazily below the form and never mount for a suggestion or in create mode —
 * there is no task id to hang them on. See `TaskSubPanels`.
 *
 * WHY A SUGGESTION USES THE SAME FORM. `createBulkTasks` takes the same
 * `CreateTaskData` as single create, so title, description, status, priority,
 * due date and assignee all ride the accept call for free. Attachments cannot
 * — `uploadTaskAttachment` needs a task id, which does not exist until the
 * suggestion is accepted — so they appear only after Accept. Project is
 * likewise deferred: the link is made against a saved task.
 *
 * Endpoints:
 *   create    `createTask(data)`                      POST /tasks/create_task
 *   accept    `createBulkTasks([data], matterUuid)`   POST /tasks/bulk_create_tasks
 *   update    `updateTask(id, data)`                  PUT  /tasks/task/{id}
 *   complete  `completeTask(id)`
 *   archive   `archiveTask(id)`
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PiArchiveLight, PiArrowSquareOutLight, PiCheckCircleLight, PiXLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../../store';
import {
  archiveTask,
  completeTask,
  createBulkTasks,
  createTask,
  updateTask,
} from '../../../../store/actions/TasksActions';
import { fetchLinkableProjects } from '../../../../store/actions/Projects/ProjectActions';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import Button from '../../../shared/TailwindComponents/Button';
import Input from '../../../shared/TailwindComponents/Input';
import DateTimePicker from '../../../shared/TailwindComponents/DatePicker';
import Textarea from '../../../shared/TailwindComponents/Textarea';
import Dropdown from '../../../shared/TailwindComponents/Dropdown';
import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  isTaskClosed,
  taskId,
} from '../../tasks/taskConstants';
import { TaskAttachments, TaskComments, TaskCollaborators } from './TaskSubPanels';
import Spinner from '../../../shared/TailwindComponents/Spinner';
import { parseServerDate } from '../../../../utils/functions';

interface Props {
  matterUuid: string;
  /** Null in create mode. */
  task: any | null;
  /** A suggestion is not yet a task: Accept creates it, Dismiss drops it. */
  isSuggestion: boolean;
  canEdit: boolean;
  /** Matter collaborators — assignee options, free, already in props. */
  matterCollaborators: any[];
  onSaved: () => void;
  /**
   * Remove this suggestion — local state AND the persisted list. Awaited, so
   * the accept can sequence the refresh after the write lands.
   */
  onDismissSuggestion?: () => void | Promise<void>;
  onClose: () => void;
}

/** The form's own shape, so a suggestion and a task edit identically. */
interface TaskDraft {
  title: string;
  description: string;
  status: string;
  priority: string;
  /** One ISO string — the picker owns both halves. */
  due: string;
  project: string;
}

const toDraft = (task: any | null): TaskDraft => {
  const rawDue = task?.due_date || task?.TaskDeadline || task?.DueDate || '';
  const parsed = parseServerDate(rawDue);
  const due = parsed ? parsed.toISOString() : '';
  return {
    title: task?.title || task?.TaskTitle || task?.Title || '',
    description: task?.description || task?.TaskDescription || '',
    status: (task?.status || task?.Status || 'todo').toLowerCase(),
    priority: (task?.priority || task?.Priority || 'medium').toLowerCase(),
    due,
    project: String(task?.project_uuid || ''),
  };
};

const TaskDetail: React.FC<Props> = ({
  matterUuid,
  task,
  isSuggestion,
  canEdit,
  matterCollaborators,
  onSaved,
  onDismissSuggestion,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const isCreate = !task;
  const [draft, setDraft] = useState<TaskDraft>(() => toDraft(task));
  const [busy, setBusy] = useState<'' | 'save' | 'complete' | 'archive'>('');
  /** Synchronous re-entry guard — see `save`. */
  const inFlight = useRef(false);
  const [error, setError] = useState('');

  /*
    The task's id, from the SAME resolver the list rows use — a second
    hand-rolled `uuid || TaskID || id` chain here is how the two drift apart,
    and it is what put every row on the same empty id before.
  */
  const identity = taskId(task) || (isCreate ? 'new' : '');

  /*
    Reload the form only when the SELECTED task changes.

    Keyed on identity, not the object: the tasks list refetches in the
    background (tab switches, `onUpdate` after any mutation) and a new array
    identity each time would reset the form under whoever is typing in it.
  */
  useEffect(() => {
    setDraft(toDraft(task));
    setError('');
  }, [identity]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof TaskDraft>(key: K, value: TaskDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  /*
    NO ASSIGNEE PICKER.

    People on a task are managed in ONE place — the Collaborators section
    below, which is the server's own list. A separate assignee dropdown
    alongside it asked the same question twice and let the two disagree.

    `assigned_to_user_uuid` is simply not sent from here, so an existing
    assignment survives the partial update rather than being clobbered.
  */

  /* ── Projects: lazy, on first open of a real task ────────────── */

  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  useEffect(() => {
    // A suggestion cannot hold a project link yet, so do not pay for the list.
    if (isSuggestion || projectsLoaded) return;
    let cancelled = false;
    dispatch(fetchLinkableProjects())
      .then((rows: any) => { if (!cancelled) setProjects(Array.isArray(rows) ? rows : []); })
      .catch(() => { /* the picker just stays empty */ })
      .finally(() => { if (!cancelled) setProjectsLoaded(true); });
    return () => { cancelled = true; };
  }, [dispatch, isSuggestion, projectsLoaded]);

  const projectOptions = useMemo(() => ([
    { value: '', label: t('No project') },
    ...projects.map((p: any) => ({
      value: String(p.uuid || p.project_uuid || p.id),
      label: p.name || p.title || t('Untitled project'),
    })),
  ]), [projects, t]);

  /* ── Writes ──────────────────────────────────────────────────── */

  const payload = () => ({
    title: draft.title.trim(),
    description: draft.description,
    status: draft.status,
    priority: draft.priority,
    ...(draft.due ? { due_date: draft.due } : {}),
    ...(draft.project && !isSuggestion ? { project_uuid: draft.project } : {}),
  });

  const fail = (message: string) => dispatch(showFeedbackModal({
    showModal: true, modalType: 'error', message, duration: 3000,
  }));

  const save = async () => {
    if (!draft.title.trim()) {
      setError(t('A title is required'));
      return;
    }
    // `busy` is state and does not update until the next render, so two fast
    // clicks could both get past it and create the task twice.
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('save');
    try {
      if (isSuggestion) {
        /*
          ACCEPT, IN ORDER. Each step depends on the one before it:

            1. create the real task — the bulk endpoint links it to the matter
            2. AWAIT the suggestion's removal, which persists the remaining
               list; skipping the await let step 4's refetch read a backend
               list that still held this suggestion, so it came back and the
               same task appeared in both Suggestions and Active
            3. say so
            4. refresh — now reading the persisted list
            5. close, which clears the selection off a row that is gone
        */
        await dispatch(createBulkTasks([payload()], matterUuid));
        await onDismissSuggestion?.();
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'success', message: t('Task created'), duration: 2500,
        }));
        onSaved();
        onClose();
      } else if (isCreate) {
        await dispatch(createTask({ ...payload(), matter_uuid: matterUuid } as any));
        onSaved();
        onClose();
      } else {
        await dispatch(updateTask(identity, payload()));
        onSaved();
      }
    } catch {
      fail(t('Failed to save task'));
    } finally {
      inFlight.current = false;
      setBusy('');
    }
  };

  const complete = async () => {
    setBusy('complete');
    try {
      await dispatch(completeTask(identity));
      onSaved();
    } catch {
      fail(t('Failed to complete task'));
    } finally {
      setBusy('');
    }
  };

  const archive = async () => {
    setBusy('archive');
    try {
      await dispatch(archiveTask(identity));
      onSaved();
      onClose();
    } catch {
      fail(t('Failed to archive task'));
    } finally {
      setBusy('');
    }
  };

  const closed = !isSuggestion && !isCreate && isTaskClosed(draft.status);
  const disabled = !canEdit || !!busy;

  return (
    <div className="p-4 space-y-4">
      <Input
        type="text"
        label={t('Title')}
        placeholder={t('What needs doing?')}
        value={draft.title}
        onChange={(e) => { set('title', e.target.value); setError(''); }}
        error={error}
        disabled={disabled}
        className="w-full"
      />

      <Textarea
        id={`task-desc-${identity}`}
        label={t('Description')}
        placeholder={t('Add detail…')}
        value={draft.description}
        onChange={(e) => set('description', e.target.value)}
        disabled={disabled}
        rows={5}
        className="w-full"
      />

      <div className="grid grid-cols-2 gap-3">
        <Dropdown
          id={`task-status-${identity}`}
          label={t('Status')}
          options={TASK_STATUSES.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={draft.status}
          placeholder={t('Select a status')}
          onChange={(v) => set('status', (Array.isArray(v) ? v[0] : v) as string)}
          fullWidth
        />
        <Dropdown
          id={`task-priority-${identity}`}
          label={t('Priority')}
          options={TASK_PRIORITIES.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={draft.priority}
          placeholder={t('Select a priority')}
          onChange={(v) => set('priority', (Array.isArray(v) ? v[0] : v) as string)}
          fullWidth
        />
      </div>

      {/*
        ONE control for date AND time — the same `DateTimePicker` BaseForm
        renders for a `datePicker` field (`showTimeSelect`, `MMM d, yyyy h:mm aa`).
        Two native inputs meant two pickers, two formats, and a time field
        that had to sit disabled until a date existed.
      */}
      <DateTimePicker
        id={`task-due-${identity}`}
        label={t('Due')}
        selected={draft.due ? new Date(draft.due) : null}
        onChange={(date) => set('due', date ? date.toISOString() : '')}
        placeholder={t('No due date')}
        disabled={disabled}
        dateFormat="MMM d, yyyy h:mm aa"
        showTimeSelect
        fullWidth
      />

      {/* A suggestion has no task id yet, so it cannot hold a project link. */}
      {!isSuggestion && (
        <Dropdown
          id={`task-project-${identity}`}
          label={t('Project')}
          options={projectOptions}
          value={draft.project}
          onChange={(v) => set('project', (Array.isArray(v) ? v[0] : v) as string)}
          placeholder={projectsLoaded ? t('No project') : t('Loading...')}
          fullWidth
        />
      )}

      {isSuggestion && (
        <p className="text-[11px] text-fg-muted">
          {t('Attachments, collaborators and project become available once this is accepted.')}
        </p>
      )}

      {/* Only a saved task has an id to hang these on. */}
      {!isSuggestion && !isCreate && (
        <>
          <TaskCollaborators
            taskId={String(identity)}
            canEdit={canEdit}
            candidates={matterCollaborators}
          />
          <TaskComments taskId={String(identity)} canEdit={canEdit} />
          <TaskAttachments taskId={String(identity)} canEdit={canEdit} />
        </>
      )}

      {/*
        A saved task also exists as a page of its own, with the history,
        related items and wider layout that do not fit in a docked pane.
        A suggestion has no page — it has no id until it is accepted — and
        neither does a task being created. Outside the `canEdit` block: a
        viewer cannot change the task but can certainly read it.
      */}
      {!isSuggestion && !isCreate && identity && (
        <div className="pt-1">
          <Button
            variant="tertiary"
            size="small"
            onClick={() => history.push(`/tasks/${identity}`)}
            icon={<PiArrowSquareOutLight className="h-3.5 w-3.5" />}
          >
            {t('Open task page')}
          </Button>
        </div>
      )}

      {canEdit && (
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-hairline-soft">
          <div className="flex items-center gap-2">
            {isSuggestion ? (
              <Button
                variant="destructive"
                size="small"
                onClick={async () => {
                  // Persisted for the same reason accept is: a local-only
                  // dismissal reappears on the next fetch.
                  if (busy) return;
                  setBusy('save');
                  try {
                    await onDismissSuggestion?.();
                    onSaved();
                    onClose();
                  } finally {
                    setBusy('');
                  }
                }}
                disabled={!!busy}
                icon={<PiXLight className="h-3.5 w-3.5" />}
              >
                {t('Dismiss')}
              </Button>
            ) : !isCreate && !closed ? (
              <Button
                variant="destructive"
                size="small"
                onClick={archive}
                disabled={!!busy}
                icon={busy === 'archive'
                  ? <Spinner size="glyph" className="!h-4 !w-4" />
                  : <PiArchiveLight className="h-3.5 w-3.5" />}
              >
                {t('Archive')}
              </Button>
            ) : <span />}
          </div>

          <div className="flex items-center gap-2">
            {!isSuggestion && !isCreate && !closed && (
              <Button
                variant="outline"
                size="small"
                onClick={complete}
                disabled={!!busy}
                icon={busy === 'complete'
                  ? <Spinner size="glyph" className="!h-4 !w-4" />
                  : <PiCheckCircleLight className="h-3.5 w-3.5" />}
              >
                {t('Complete')}
              </Button>
            )}
            <Button
              variant="primary"
              size="small"
              onClick={save}
              disabled={!!busy}
              icon={busy === 'save' ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
            >
              {isSuggestion ? t('Accept') : isCreate ? t('Create') : t('Save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;
